import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrainingDrill, DrillDifficulty, DrillCategory, AgeGroup, DrillImageMetadata, DrillVideoMetadata } from './Model';
import { restClient } from './RestClient';
import { AuthService } from './AuthService';
import { YouTubeEmbed } from './YouTubeEmbed';
import { useReferenceData } from './ReferenceDataContext';

interface EditDrillProps {
  onError?: (message: string) => void;
}

type MediaType = 'youtube' | 'images' | 'video';

const EMPTY_DRILL: TrainingDrill = {
  id: 0,
  name: '',
  description: '',
  youtubeUrl: '',
  difficulty: 'Intermediate',
  playersRequired: 4,
  duration: 10,
  skills: [],
  ageGroups: [],
  equipment: {},
  spaceRequired: { width: 25, height: 25 },
  category: 'Main',
};

const MAX_IMAGES = 5;

export const EditDrill: React.FC<EditDrillProps> = ({ onError }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { referenceData, currentSport, currentSportConfig } = useReferenceData();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoFileInputRef = useRef<HTMLInputElement>(null);

  const isCreateMode = id === 'new';

  const [drill, setDrill] = useState<TrainingDrill | null>(isCreateMode ? { ...EMPTY_DRILL, sport: currentSport } : null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [loading, setLoading] = useState(!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [fetchingYouTube, setFetchingYouTube] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<MediaType>('youtube');
  const [savedYoutubeUrl, setSavedYoutubeUrl] = useState<string | undefined>();

  // Image state
  const [existingImages, setExistingImages] = useState<DrillImageMetadata[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const previewUrlsRef = useRef<string[]>([]);

  // Video state
  const [existingVideo, setExistingVideo] = useState<DrillVideoMetadata | null>(null);
  const [pendingVideoFile, setPendingVideoFile] = useState<File | null>(null);
  const [pendingVideoPreview, setPendingVideoPreview] = useState<string | null>(null);

  const isAdmin = AuthService.isAdmin();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/training-drills');
      return;
    }

    if (isCreateMode) {
      setLoading(false);
      return;
    }

    const fetchDrill = async () => {
      if (!id) {
        setError('Invalid drill ID');
        setLoading(false);
        return;
      }

      const drillId = parseInt(id, 10);
      const result = await restClient.getDrill(drillId);
      if (result.IsSuccess) {
        const loadedDrill = result.Value;
        setDrill(loadedDrill);

        // Determine media type from drill data
        if (loadedDrill.mediaType === 'images') {
          setMediaType('images');
        } else if (loadedDrill.mediaType === 'video') {
          setMediaType('video');
        } else {
          setMediaType('youtube');
        }

        // Load existing images
        const imgResult = await restClient.getDrillImageMetadata(drillId);
        if (imgResult.IsSuccess) {
          setExistingImages(imgResult.Value);
          if (imgResult.Value.length > 0 && !loadedDrill.mediaType) {
            setMediaType('images');
          }
        }

        // Load existing video
        const vidResult = await restClient.getDrillVideoMetadata(drillId);
        if (vidResult.IsSuccess && vidResult.Value) {
          setExistingVideo(vidResult.Value);
          if (!loadedDrill.mediaType) {
            setMediaType('video');
          }
        }
      } else {
        setError(result.Error?.description ?? 'Failed to load drill');
        onError?.(result.Error?.description ?? 'Failed to load drill');
      }
      setLoading(false);
    };

    fetchDrill();
  }, [id, isAdmin, isCreateMode, navigate, onError]);

  // Clean up preview URLs on unmount only
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      if (pendingVideoPreview) URL.revokeObjectURL(pendingVideoPreview);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFetchYouTube = async () => {
    if (!youtubeUrl.trim()) {
      setError('Please enter a YouTube URL');
      return;
    }

    setFetchingYouTube(true);
    setError(null);

    const result = await restClient.fetchYouTubeDetails(youtubeUrl, currentSport);
    if (result.IsSuccess) {
      const info = result.Value;
      setDrill(prev => prev ? {
        ...prev,
        name: info.title,
        description: info.description,
        youtubeUrl: info.youtubeUrl,
        duration: info.duration,
        difficulty: info.inferredDifficulty,
        category: info.inferredCategory,
        skills: info.inferredSkills,
        ageGroups: info.inferredAgeGroups,
        playersRequired: info.inferredPlayersRequired,
        equipment: info.inferredEquipment,
        sport: currentSport,
        mediaType: 'youtube',
      } : null);
      setMediaType('youtube');
    } else {
      setError(result.Error.description ?? 'Failed to fetch YouTube video details');
    }
    setFetchingYouTube(false);
  };

  const handleMediaTypeChange = (newType: MediaType) => {
    if (drill && (newType === 'images' || newType === 'video') && drill.youtubeUrl) {
      setSavedYoutubeUrl(drill.youtubeUrl);
    }
    if (drill && newType === 'youtube' && savedYoutubeUrl && !drill.youtubeUrl) {
      setDrill({ ...drill, mediaType: newType, youtubeUrl: savedYoutubeUrl });
      setMediaType(newType);
      return;
    }
    setMediaType(newType);
    if (drill) {
      setDrill({ ...drill, mediaType: newType });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const totalCount = existingImages.length + pendingFiles.length + files.length;
    if (totalCount > MAX_IMAGES) {
      setError(`Maximum ${MAX_IMAGES} images per drill. You can add ${MAX_IMAGES - existingImages.length - pendingFiles.length} more.`);
      return;
    }

    // Validate file types and sizes
    for (const file of files) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError(`File "${file.name}" is not a supported image type. Use JPEG, PNG, or WebP.`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(`File "${file.name}" exceeds the 5MB size limit.`);
        return;
      }
    }

    setError(null);
    const newPreviews = files.map(f => URL.createObjectURL(f));
    previewUrlsRef.current = [...previewUrlsRef.current, ...newPreviews];
    setPendingFiles(prev => [...prev, ...files]);
    setPendingPreviews(prev => [...prev, ...newPreviews]);

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index: number) => {
    const url = pendingPreviews[index];
    URL.revokeObjectURL(url);
    previewUrlsRef.current = previewUrlsRef.current.filter(u => u !== url);
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
    setPendingPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageId: number) => {
    if (!drill) return;
    const result = await restClient.deleteDrillImage(drill.id, imageId);
    if (result.IsSuccess) {
      setExistingImages(prev => prev.filter(img => img.id !== imageId));
    } else {
      setError(result.Error?.description ?? 'Failed to delete image');
    }
  };

  const handleVideoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['video/mp4', 'video/webm', 'video/quicktime'].includes(file.type)) {
      setError(`File "${file.name}" is not a supported video type. Use MP4, WebM, or MOV.`);
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError(`File "${file.name}" exceeds the 50MB size limit.`);
      return;
    }

    setError(null);
    if (pendingVideoPreview) URL.revokeObjectURL(pendingVideoPreview);
    const previewUrl = URL.createObjectURL(file);
    setPendingVideoFile(file);
    setPendingVideoPreview(previewUrl);

    if (videoFileInputRef.current) videoFileInputRef.current.value = '';
  };

  const removePendingVideo = () => {
    if (pendingVideoPreview) URL.revokeObjectURL(pendingVideoPreview);
    setPendingVideoFile(null);
    setPendingVideoPreview(null);
  };

  const removeExistingVideo = async () => {
    if (!drill || !existingVideo) return;
    const result = await restClient.deleteDrillVideo(drill.id, existingVideo.id);
    if (result.IsSuccess) {
      setExistingVideo(null);
    } else {
      setError(result.Error?.description ?? 'Failed to delete video');
    }
  };

  const handleSave = async () => {
    if (!drill) return;

    if (!drill.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!drill.description.trim()) {
      setError('Description is required');
      return;
    }
    if (drill.skills.length === 0) {
      setError('At least one skill must be selected');
      return;
    }
    if (drill.ageGroups.length === 0) {
      setError('At least one age group must be selected');
      return;
    }
    if (isCreateMode && mediaType === 'youtube' && !drill.youtubeUrl?.trim()) {
      setError('YouTube URL is required. Please fetch video details first.');
      return;
    }
    if (mediaType === 'images') {
      const totalImages = isCreateMode ? pendingFiles.length : existingImages.length + pendingFiles.length;
      if (totalImages === 0) {
        setError('Please add at least one image.');
        return;
      }
    }
    if (mediaType === 'video') {
      const hasVideo = isCreateMode ? !!pendingVideoFile : (!!existingVideo || !!pendingVideoFile);
      if (!hasVideo) {
        setError('Please upload a video file.');
        return;
      }
    }

    setSaving(true);
    setError(null);

    const drillToSave = {
      ...drill,
      mediaType,
      youtubeUrl: (mediaType === 'images' || mediaType === 'video') ? undefined : drill.youtubeUrl,
    };

    if (isCreateMode) {
      const result = await restClient.createDrill(drillToSave);
      if (result.IsSuccess) {
        const createdDrill = result.Value;
        // Upload pending images if media type is images
        if (mediaType === 'images' && pendingFiles.length > 0) {
          const uploadResult = await restClient.uploadDrillImages(createdDrill.id, pendingFiles);
          if (uploadResult.IsFailure) {
            setError(uploadResult.Error?.description ?? 'Drill created but image upload failed.');
            setSaving(false);
            return;
          }
        }
        // Upload pending video if media type is video
        if (mediaType === 'video' && pendingVideoFile) {
          const uploadResult = await restClient.uploadDrillVideo(createdDrill.id, pendingVideoFile);
          if (uploadResult.IsFailure) {
            setError(uploadResult.Error?.description ?? 'Drill created but video upload failed.');
            setSaving(false);
            return;
          }
        }
        navigate('/training-drills', { state: { message: 'Drill created successfully!' } });
      } else {
        setError(result.Error?.description ?? 'Failed to create drill');
      }
    } else {
      const result = await restClient.updateDrill(parseInt(id!, 10), drillToSave);
      if (result.IsSuccess) {
        // Upload any new pending images
        if (mediaType === 'images' && pendingFiles.length > 0) {
          const uploadResult = await restClient.uploadDrillImages(drill.id, pendingFiles);
          if (uploadResult.IsFailure) {
            setError(uploadResult.Error?.description ?? 'Drill updated but image upload failed.');
            setSaving(false);
            return;
          }
        }
        // Upload pending video if media type is video
        if (mediaType === 'video' && pendingVideoFile) {
          const uploadResult = await restClient.uploadDrillVideo(drill.id, pendingVideoFile);
          if (uploadResult.IsFailure) {
            setError(uploadResult.Error?.description ?? 'Drill updated but video upload failed.');
            setSaving(false);
            return;
          }
        }
        navigate('/training-drills', { state: { message: 'Drill updated successfully!' } });
      } else {
        setError(result.Error?.description ?? 'Failed to save drill');
      }
    }
    setSaving(false);
  };

  const handleCancel = () => {
    navigate('/training-drills');
  };

  const updateField = <K extends keyof TrainingDrill>(field: K, value: TrainingDrill[K]) => {
    if (drill) {
      setDrill({ ...drill, [field]: value });
    }
  };

  const updateEquipment = (field: string, value: number) => {
    if (drill) {
      setDrill({
        ...drill,
        equipment: { ...drill.equipment, [field]: value }
      });
    }
  };

  const updateSpaceRequired = (field: 'width' | 'height', value: number) => {
    if (drill) {
      setDrill({
        ...drill,
        spaceRequired: { ...drill.spaceRequired, [field]: value }
      });
    }
  };

  const toggleSkill = (skill: string) => {
    if (drill) {
      const skills = drill.skills.includes(skill)
        ? drill.skills.filter(s => s !== skill)
        : [...drill.skills, skill];
      setDrill({ ...drill, skills });
    }
  };

  const toggleAgeGroup = (ageGroup: AgeGroup) => {
    if (drill) {
      const ageGroups = drill.ageGroups.includes(ageGroup)
        ? drill.ageGroups.filter(ag => ag !== ageGroup)
        : [...drill.ageGroups, ageGroup];
      setDrill({ ...drill, ageGroups });
    }
  };

  const canAddMoreImages = existingImages.length + pendingFiles.length < MAX_IMAGES;

  if (loading) {
    return (
      <div className="card">
        <div className="card-body loading-state">
          <p className="text-muted text-lg m-0">Loading drill...</p>
        </div>
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="card">
        <div className="card-body loading-state">
          <p className="text-danger text-lg m-0">{error || 'Drill not found'}</p>
          <button onClick={() => navigate('/training-drills')} className="btn btn-secondary mt-4">
            Back to Drills
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-800 mx-auto">
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title">{isCreateMode ? 'Add New Drill' : 'Edit Drill'}</h2>
        <p className="page-description">
          {isCreateMode
            ? 'Add a drill with a YouTube video or upload images.'
            : 'Modify the drill metadata below.'}
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-error mb-4">{error}</div>
      )}

      {/* Form */}
      <div className="card">
        <div className="card-body-lg">
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            {/* Name */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">Name *</label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={drill.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
                maxLength={100}
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description" className="form-label">Description *</label>
              <textarea
                id="description"
                className="form-control"
                value={drill.description}
                onChange={(e) => updateField('description', e.target.value)}
                required
              />
            </div>

            {/* Media Type Selector */}
            <div className="form-group">
              <label className="form-label">Media Type</label>
              <div className="media-type-selector">
                <label className={`media-type-option ${mediaType === 'youtube' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="mediaType"
                    value="youtube"
                    checked={mediaType === 'youtube'}
                    onChange={() => handleMediaTypeChange('youtube')}
                  />
                  YouTube Video
                </label>
                <label className={`media-type-option ${mediaType === 'images' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="mediaType"
                    value="images"
                    checked={mediaType === 'images'}
                    onChange={() => handleMediaTypeChange('images')}
                  />
                  Images
                </label>
                <label className={`media-type-option ${mediaType === 'video' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="mediaType"
                    value="video"
                    checked={mediaType === 'video'}
                    onChange={() => handleMediaTypeChange('video')}
                  />
                  Upload Video
                </label>
              </div>
            </div>

            {/* YouTube URL / Video (shown when mediaType is youtube) */}
            {mediaType === 'youtube' && (
              <div className="form-group">
                <label htmlFor="youtubeUrl" className="form-label">
                  {isCreateMode ? 'YouTube URL' : 'Video Reference'}
                </label>
                {isCreateMode ? (
                  <>
                    <div className="input-group">
                      <input
                        type="text"
                        id="youtubeUrl"
                        className="form-control"
                        value={youtubeUrl}
                        onChange={(e) => setYoutubeUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                      <button
                        type="button"
                        onClick={handleFetchYouTube}
                        disabled={fetchingYouTube || !youtubeUrl.trim()}
                        className={`btn ${fetchingYouTube || !youtubeUrl.trim() ? 'btn-secondary' : 'btn-info'}`}
                      >
                        {fetchingYouTube ? 'Fetching...' : 'Fetch Details'}
                      </button>
                    </div>
                    <p className="form-help">Enter the YouTube URL and click "Fetch Details" to populate the form.</p>
                    {drill.youtubeUrl && (
                      <div className="mt-3">
                        <YouTubeEmbed url={drill.youtubeUrl} title={drill.name || 'Video preview'} borderRadius={4} />
                      </div>
                    )}
                  </>
                ) : drill.youtubeUrl ? (
                  <YouTubeEmbed url={drill.youtubeUrl} title={`Video for ${drill.name}`} borderRadius={4} />
                ) : (
                  <div className="card loading-state">
                    <p className="text-muted m-0">No video available for this drill</p>
                  </div>
                )}
              </div>
            )}

            {/* Image Upload (shown when mediaType is images) */}
            {mediaType === 'images' && (
              <div className="form-group">
                <label className="form-label">
                  Images ({existingImages.length + pendingFiles.length}/{MAX_IMAGES})
                </label>

                {/* Existing images */}
                {existingImages.length > 0 && (
                  <div className="drill-image-preview-grid">
                    {existingImages.map(img => (
                      <div key={img.id} className="drill-image-preview-item">
                        <img
                          src={restClient.getDrillImageUrl(drill.id, img.id)}
                          alt={img.fileName}
                        />
                        <button
                          type="button"
                          className="drill-image-preview-remove"
                          onClick={() => removeExistingImage(img.id)}
                          aria-label={`Remove ${img.fileName}`}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pending file previews */}
                {pendingPreviews.length > 0 && (
                  <div className="drill-image-preview-grid">
                    {pendingPreviews.map((previewUrl, idx) => (
                      <div key={idx} className="drill-image-preview-item">
                        <img src={previewUrl} alt={pendingFiles[idx].name} />
                        <button
                          type="button"
                          className="drill-image-preview-remove"
                          onClick={() => removePendingFile(idx)}
                          aria-label={`Remove ${pendingFiles[idx].name}`}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload area */}
                {canAddMoreImages && (
                  <div
                    className="drill-image-upload-area mt-3"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={handleFileSelect}
                    />
                    <p className="text-muted m-0">
                      Click to add images (JPEG, PNG, WebP, max 5MB each)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Video Upload (shown when mediaType is video) */}
            {mediaType === 'video' && (
              <div className="form-group">
                <label className="form-label">Video</label>

                {/* Existing video */}
                {existingVideo && !pendingVideoFile && (
                  <div className="mb-3">
                    <video
                      className="drill-video-player"
                      controls
                      src={restClient.getDrillVideoUrl(drill.id, existingVideo.id)}
                    />
                    <div className="mt-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={removeExistingVideo}
                      >
                        Remove Video
                      </button>
                      <span className="text-muted text-sm ml-2">{existingVideo.fileName}</span>
                    </div>
                  </div>
                )}

                {/* Pending video preview */}
                {pendingVideoPreview && (
                  <div className="mb-3">
                    <video
                      className="drill-video-player"
                      controls
                      src={pendingVideoPreview}
                    />
                    <div className="mt-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={removePendingVideo}
                      >
                        Remove
                      </button>
                      <span className="text-muted text-sm ml-2">{pendingVideoFile?.name}</span>
                    </div>
                  </div>
                )}

                {/* Upload area */}
                {!existingVideo && !pendingVideoFile && (
                  <div
                    className="drill-image-upload-area mt-3"
                    onClick={() => videoFileInputRef.current?.click()}
                  >
                    <input
                      ref={videoFileInputRef}
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,.mov"
                      onChange={handleVideoFileSelect}
                    />
                    <p className="text-muted m-0">
                      Click to upload a video (MP4 or WebM, max 50MB)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Difficulty and Category in a row */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="difficulty" className="form-label">Difficulty *</label>
                <select
                  id="difficulty"
                  className="form-select"
                  value={drill.difficulty}
                  onChange={(e) => updateField('difficulty', e.target.value as DrillDifficulty)}
                  required
                >
                  {(referenceData?.difficulties ?? []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="category" className="form-label">Category *</label>
                <select
                  id="category"
                  className="form-select"
                  value={drill.category}
                  onChange={(e) => updateField('category', e.target.value as DrillCategory)}
                  required
                >
                  {(referenceData?.categories ?? []).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Duration and Players in a row */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="duration" className="form-label">Duration (minutes) *</label>
                <input
                  type="number"
                  id="duration"
                  className="form-control"
                  value={drill.duration}
                  onChange={(e) => updateField('duration', parseInt(e.target.value) || 0)}
                  required
                  min={1}
                  max={120}
                />
              </div>

              <div>
                <label htmlFor="playersRequired" className="form-label">Players Required *</label>
                <input
                  type="number"
                  id="playersRequired"
                  className="form-control"
                  value={drill.playersRequired}
                  onChange={(e) => updateField('playersRequired', parseInt(e.target.value) || 1)}
                  required
                  min={1}
                  max={30}
                />
              </div>
            </div>

            {/* Skills - Multi-select with checkboxes */}
            <div className="form-group">
              <label className="form-label">Skills *</label>
              <div className="form-check-grid">
                {(currentSportConfig?.skills ?? referenceData?.skills ?? []).map(skill => (
                  <label key={skill} className="form-check">
                    <input
                      type="checkbox"
                      checked={drill.skills.includes(skill)}
                      onChange={() => toggleSkill(skill)}
                    />
                    <span>{skill}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Age Groups - Multi-select with checkboxes */}
            <div className="form-group">
              <label className="form-label">Age Groups *</label>
              <div className="form-check-grid form-check-grid-wide">
                {(referenceData?.ageGroups ?? []).map(({ value, label }) => (
                  <label key={value} className="form-check">
                    <input
                      type="checkbox"
                      checked={drill.ageGroups.includes(value)}
                      onChange={() => toggleAgeGroup(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Equipment Section */}
            <fieldset className="form-fieldset">
              <legend>Equipment</legend>
              <div className="equipment-grid">
                {(currentSportConfig?.equipmentOptions ?? []).map(item => {
                  const fieldId = `equipment-${item.toLowerCase().replace(/\s+/g, '-')}`;
                  return (
                    <div key={item}>
                      <label htmlFor={fieldId} className="form-label">{item}</label>
                      <input
                        type="number"
                        id={fieldId}
                        className="form-control"
                        value={drill.equipment[item] || 0}
                        onChange={(e) => updateEquipment(item, parseInt(e.target.value) || 0)}
                        min={0}
                      />
                    </div>
                  );
                })}
              </div>
            </fieldset>

            {/* Space Required */}
            <fieldset className="form-fieldset">
              <legend>Space Required (meters)</legend>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="spaceWidth" className="form-label">Width</label>
                  <input
                    type="number"
                    id="spaceWidth"
                    className="form-control"
                    value={drill.spaceRequired.width || 0}
                    onChange={(e) => updateSpaceRequired('width', parseInt(e.target.value) || 0)}
                    min={1}
                    max={200}
                  />
                </div>

                <div>
                  <label htmlFor="spaceHeight" className="form-label">Height</label>
                  <input
                    type="number"
                    id="spaceHeight"
                    className="form-control"
                    value={drill.spaceRequired.height || 0}
                    onChange={(e) => updateSpaceRequired('height', parseInt(e.target.value) || 0)}
                    min={1}
                    max={200}
                  />
                </div>
              </div>
            </fieldset>

            {/* Hidden Toggle (edit mode only) */}
            {!isCreateMode && (
              <div className="form-group">
                <label className="form-check">
                  <input
                    type="checkbox"
                    checked={drill.isHidden ?? false}
                    onChange={(e) => updateField('isHidden', e.target.checked)}
                  />
                  <span>Hidden</span>
                </label>
                <p className="form-help">Hidden drills are excluded from session plans and the drills library.</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                type="submit"
                disabled={saving}
                className={`btn ${saving ? 'btn-secondary' : 'btn-primary'}`}
              >
                {saving ? 'Saving...' : (isCreateMode ? 'Create Drill' : 'Save Changes')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
